import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Camera, Trash2, Mail, AlertTriangle, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER, INPUT_CLASS, typeMeta } from './theme';
import { compressImage } from '../../lib/imageCompress';
import { AuthThumb } from './AuthThumb';
import { DiaryPhotoLightbox, entryPhotoItems } from './DiaryPhotoLightbox';
import { LabourForm } from './forms/LabourForm';
import { MaterialsForm } from './forms/MaterialsForm';
import {
  DeliveryForm, WeatherForm, DelayForm,
  RfiForm, InstructionForm, VisitForm,
} from './forms/SimpleForms';

/**
 * EntryDialog — full-screen modal that captures a single diary entry.
 *
 * Warm-paper theme. Each type-specific form is above the shared Title +
 * Notes inputs so users hit the critical fields first.
 *
 * Draft auto-save (§4C): every keystroke persists to localStorage keyed
 * by `{projectId}|{date}|{type}`. Restored automatically if the user
 * closes without saving. Cleared on successful save.
 *
 * Photos (Phase 3) are queued client-side and uploaded AFTER the entry
 * is created (we need the entry id first). Each queued item: { key,
 * file, previewUrl }.
 */

const draftKey = (projectId, date, type) => `lux_diary_draft:${projectId}|${date}|${type}`;

export const EntryDialog = ({ type, projectId, date, entry, initialPayload, onClose, onSaved, onDeleteRequest, onPhotosChanged, canEdit = true, canDelete = false }) => {
  const { api } = useAuth();
  // In edit mode the `type` comes from the entry (so pre-existing Delivery
  // entries still get their DeliveryForm), not the caller.
  const activeType = entry ? entry.type : type;
  const meta = typeMeta(activeType);
  const Icon = meta.icon;
  const isEditing = Boolean(entry);
  const readOnly = isEditing && !canEdit;

  const key = useMemo(() => draftKey(projectId, date, activeType), [projectId, date, activeType]);

  // Restore any previous unsaved draft on mount — but ONLY when creating.
  // In edit mode we always hydrate from the actual entry. When the caller
  // seeds an `initialPayload` (e.g. "Log this crew" from the schedule nudge)
  // that wins for a fresh entry so the pre-selected people show immediately.
  const initial = useMemo(() => {
    if (isEditing) {
      return {
        title: entry.title || '',
        detail: entry.detail || '',
        payload: entry.payload || {},
      };
    }
    if (initialPayload) {
      return { title: '', detail: '', payload: initialPayload };
    }
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore corrupt draft */ }
    return { title: '', detail: '', payload: {} };
  }, [key, isEditing, entry, initialPayload]);

  const [title, setTitle] = useState(initial.title || '');
  const [detail, setDetail] = useState(initial.detail || '');
  const [payload, setPayload] = useState(initial.payload || {});
  const [saving, setSaving] = useState(false);
  const [restoredDraft] = useState(!isEditing && !initialPayload && Boolean(initial.title || initial.detail || Object.keys(initial.payload || {}).length));
  const [queuedPhotos, setQueuedPhotos] = useState([]);
  // Full-screen viewer for photos already saved on the entry.
  const [lightbox, setLightbox] = useState(null);
  // Photos binned from the viewer this session (entry prop is a snapshot).
  const [removedPhotoIds, setRemovedPhotoIds] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  // When set, the dialog swaps to the "Email ready" success view (materials flow).
  const [emailReady, setEmailReady] = useState(null); // { to, subject, body }
  // SD-2 §4 — the Order-only confirm gate before actually saving/sending.
  const [pendingOrderConfirm, setPendingOrderConfirm] = useState(false);

  const addPhotos = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const items = files.map(f => ({
      key: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
    }));
    setQueuedPhotos(prev => [...prev, ...items]);
    event.target.value = '';
  };

  const removeQueuedPhoto = (photoKey) => {
    setQueuedPhotos(prev => {
      const item = prev.find(p => p.key === photoKey);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(p => p.key !== photoKey);
    });
  };

  // Persist draft on every change — but NOT when editing (we don't want
  // an in-progress edit to become a "draft" for creation next time).
  useEffect(() => {
    if (isEditing) return;
    const isEmpty = !title.trim() && !detail.trim() && Object.keys(payload || {}).length === 0;
    try {
      if (isEmpty) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify({ title, detail, payload }));
    } catch (_e) { /* localStorage may be full or disabled — non-blocking */ }
  }, [key, title, detail, payload, isEditing]);

  const handleSave = async ({ skipOrderConfirm = false } = {}) => {
    if (readOnly) return;

    // Minimum-content gate per type.
    const hasBasic = title.trim() || detail.trim();
    const hasLabour = activeType === 'labour' && (payload?.people?.length || 0) > 0;
    const hasDelivery = activeType === 'delivery' && (payload?.supplier || payload?.materials);
    const hasWeather = activeType === 'weather' && payload?.condition;
    const hasDelay = activeType === 'delay' && payload?.cause;
    const hasRfi = (activeType === 'rfi' || activeType === 'instruction') && (payload?.raised_with || payload?.party || title.trim() || detail.trim());
    const hasVisit = activeType === 'visit' && (payload?.attendees || payload?.notes);
    const hasMaterials = activeType === 'materials' && (payload?.line_items || []).some(x => (x?.description || '').trim());
    if (!(hasBasic || hasLabour || hasDelivery || hasWeather || hasDelay || hasRfi || hasVisit || hasMaterials)) {
      toast.error('Add some detail before saving');
      return;
    }

    // SD-2 §4 — Orders always require an explicit confirm before we save
    // and prepare the email. Enquiries skip the confirm to keep repeat
    // price-hunting fast; the safety concern is committing to a purchase,
    // not asking for one.
    if (activeType === 'materials' && payload?.intent === 'order' && !skipOrderConfirm) {
      setPendingOrderConfirm(true);
      return;
    }

    setSaving(true);
    try {
      let entryDoc;

      if (isEditing) {
        // PATCH — DIA-1 §2.2 / §2.3. Server does its own permission gate.
        const patchRes = await api.patch(`/diary/entries/${entry.id}`, {
          title: (title.trim() || defaultTitleFor(activeType, payload)),
          detail: detail.trim() || null,
          payload,
        });
        entryDoc = patchRes.data;
      } else {
        const createRes = await api.post('/diary/entries', {
          project_id: projectId,
          date,
          type: activeType,
          title: (title.trim() || defaultTitleFor(activeType, payload)),
          detail: detail.trim() || null,
          payload,
          photo_ids: [],
        });
        entryDoc = createRes.data;
      }

      // Photo uploads — only for entries just created OR fresh queued photos on an edit.
      if (queuedPhotos.length) {
        setUploadProgress('compressing');
        let done = 0;
        for (const item of queuedPhotos) {
          try {
            const compressed = await compressImage(item.file);
            setUploadProgress(`uploading ${done + 1}/${queuedPhotos.length}`);
            const fd = new FormData();
            fd.append('file', compressed, compressed.name);
            await api.post(`/diary/entries/${entryDoc.id}/photos`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            done += 1;
          } catch (photoErr) {
            const detailMsg = photoErr.response?.data?.detail || 'Photo upload failed';
            toast.error(`${item.file.name}: ${detailMsg}`);
          }
        }
        queuedPhotos.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      }

      toast.success(`${meta.label} ${isEditing ? 'updated' : 'saved'}${queuedPhotos.length ? ` (with ${queuedPhotos.length} photo${queuedPhotos.length > 1 ? 's' : ''})` : ''}`);
      try { localStorage.removeItem(key); } catch (_e) { /* noop */ }

      // Materials CREATE → always show the Review-email view. On an EDIT
      // we skip the email review — the mailto has already been sent once,
      // and the user is fixing typos, not re-issuing an order.
      if (activeType === 'materials' && !isEditing) {
        try {
          const emailRes = await api.post(`/diary/entries/${entryDoc.id}/render-email`);
          setEmailReady(emailRes.data); // triggers the "Email ready" view
          onSaved && onSaved({ silent: true });
          return; // don't close — user finishes via the success view
        } catch (mailErr) {
          toast.error(mailErr.response?.data?.detail || 'Entry saved, but the email could not be prepared');
        }
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to save ${meta.label}`);
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  const renderTypeForm = () => {
    // DIA-2 §2.1 — read-only view. Pass `readOnly` through to the form so
    // it can hide any "+ Add row" / trash buttons entirely (the fieldset +
    // pointer-events guard below handles native controls / custom onClicks).
    const p = { value: payload, onChange: setPayload, readOnly };
    switch (activeType) {
      case 'labour':      return <LabourForm {...p} />;
      case 'delivery':    return <DeliveryForm {...p} />;
      case 'weather':     return <WeatherForm {...p} />;
      case 'delay':       return <DelayForm {...p} />;
      case 'rfi':         return <RfiForm {...p} />;
      case 'instruction': return <InstructionForm {...p} />;
      case 'visit':       return <VisitForm {...p} />;
      case 'materials':   return <MaterialsForm {...p} />;
      default:            return null; // note = title + detail only
    }
  };

  // DIA-2 §2.1 — read-only guard is applied inline where the form renders
  // (see below). It must NOT be a component declared inside this render, or
  // React would remount the whole form subtree on every keystroke and drop
  // input focus.

  // Photos already saved on the entry (Phase 3 → OneDrive-hosted). In
  // edit mode we show these above the "Add" queue; in read-only they are
  // rendered as a viewable gallery with a link out to OneDrive.
  const savedPhotos = isEditing && Array.isArray(entry?.photos) ? entry.photos.filter(p => !removedPhotoIds.includes(p.id)) : [];

  // Bin in the viewer — off the entry and into the OneDrive recycle bin.
  const handleDeletePhoto = async (item) => {
    try {
      const r = await api.delete(`/diary/entries/${item.entryId}/photos/${item.photoId}?delete_file=true`);
      toast.success(r.data?.file_deleted ? 'Photo removed' : 'Photo removed from the entry (file kept on OneDrive)');
      setRemovedPhotoIds(ids => [...ids, item.photoId]);
      setLightbox(prev => {
        if (!prev) return prev;
        const items = prev.items.filter(i => i.key !== item.key);
        return items.length ? { ...prev, items, index: Math.min(prev.index, items.length - 1) } : null;
      });
      onPhotosChanged && onPhotosChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove the photo');
      throw e;
    }
  };

  // Small helper — a mailto URL that iOS Mail can open with subject/body prefilled.
  // Body newlines are preserved as %0A after encodeURIComponent.
  const buildMailtoUrl = (r) => {
    const to = encodeURIComponent(r?.to || '');
    const subj = encodeURIComponent(r?.subject || '');
    const body = encodeURIComponent(r?.body || '');
    return `mailto:${to}?subject=${subj}&body=${body}`;
  };

  // Outlook app deep link (iOS/Android). Opens the Outlook compose screen
  // pre-filled when Outlook is installed. If it isn't installed the link is a
  // no-op — which is exactly why the "Default mail" mailto button stays as a
  // fallback below.
  const buildOutlookUrl = (r) => {
    const to = encodeURIComponent(r?.to || '');
    const subj = encodeURIComponent(r?.subject || '');
    const body = encodeURIComponent(r?.body || '');
    return `ms-outlook://compose?to=${to}&subject=${subj}&body=${body}`;
  };

  const finishAfterEmail = () => {
    setEmailReady(null);
    onSaved && onSaved();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      style={{ background: 'rgba(31,41,55,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={emailReady ? finishAfterEmail : onClose}
      data-testid="entry-dialog"
    >
      {lightbox && (
        // Portal children still bubble React events — keep the entry dialog open.
        <div onClick={(e) => e.stopPropagation()}>
          <DiaryPhotoLightbox
            items={lightbox.items}
            index={lightbox.index}
            canDelete={canEdit}
            onDelete={handleDeletePhoto}
            onIndexChange={(i) => setLightbox(prev => (prev ? { ...prev, index: i } : prev))}
            onClose={() => setLightbox(null)}
          />
        </div>
      )}
      {/* SD-2 §4 — ORDER confirmation. Sits above the entry dialog so the
          user's last conscious action is confirming they meant to place a
          real order. Enquiries never see this. */}
      {pendingOrderConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(31,41,55,0.7)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => e.stopPropagation()}
          data-testid="order-confirm-dialog"
        >
          <div
            className="max-w-md w-full rounded-2xl overflow-hidden"
            style={{ background: PAPER.card, border: '2px solid #16A34A', boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}
          >
            <div className="p-5 flex gap-3 items-start">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#DCFCE7', color: '#166534' }}
              >
                <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#16A34A' }}>
                  Confirm Order
                </div>
                <div className="text-[16px] font-bold mt-0.5" style={{ color: PAPER.ink }}>
                  You&apos;re about to send an{' '}
                  <span style={{ color: '#16A34A' }}>ORDER</span> to {payload?.supplier_name || 'this supplier'}.
                </div>
                <div className="text-[13px] mt-2" style={{ color: PAPER.inkSoft }}>
                  This tells the supplier to supply — no quote will be requested. Cancel to change to an Enquiry instead.
                </div>
              </div>
            </div>
            <div
              className="px-5 py-3 flex gap-2 justify-end"
              style={{ background: PAPER.page, borderTop: `1px solid ${PAPER.border}` }}
            >
              <button
                type="button"
                onClick={() => setPendingOrderConfirm(false)}
                disabled={saving}
                className="text-[13px] font-medium rounded-lg px-4 py-2 hover:bg-[#FAF7F2] disabled:opacity-50"
                style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                data-testid="order-confirm-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setPendingOrderConfirm(false); handleSave({ skipOrderConfirm: true }); }}
                disabled={saving}
                className="text-[13px] font-semibold rounded-lg px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: '#16A34A', color: '#FFFFFF' }}
                data-testid="order-confirm-continue-btn"
              >
                Send order
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="w-full max-w-lg max-h-[95vh] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: PAPER.page, border: `1px solid ${PAPER.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {emailReady ? (
          // ============ Email Ready view (materials post-save) ============
          <>
            <div
              className="flex items-center gap-3 px-4 py-3.5"
              style={{ background: PAPER.card, borderBottom: `1px solid ${PAPER.border}` }}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: '#FFEDD5', color: '#C2410C' }}
              >
                <Mail className="w-4 h-4" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
                  Ready to send
                </div>
                <div className="text-[15px] font-semibold" style={{ color: PAPER.ink }}>Review email</div>
              </div>
              <button
                type="button"
                onClick={finishAfterEmail}
                className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
                style={{ color: PAPER.muted }}
                data-testid="entry-dialog-email-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              <div className="text-[12px]" style={{ color: PAPER.inkSoft }}>
                We&apos;ve saved the entry and prepared a draft email. Tap
                <b> Open in Outlook</b> to compose it in the Outlook app (or use
                <b> Default mail</b> for your device&apos;s standard mail app).
                Review, edit if needed, then hit Send.
              </div>
              <FieldRow label="To">
                <div
                  className="rounded-md px-3 py-2 text-[13px]"
                  style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                  data-testid="email-preview-to"
                >{emailReady.to || <span style={{ color: PAPER.hint }}>(no email on the supplier — add one first)</span>}</div>
              </FieldRow>
              <FieldRow label="Subject">
                <div
                  className="rounded-md px-3 py-2 text-[13px]"
                  style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                  data-testid="email-preview-subject"
                >{emailReady.subject}</div>
              </FieldRow>
              <FieldRow label="Message">
                <pre
                  className="rounded-md px-3 py-2.5 text-[12px] whitespace-pre-wrap font-sans leading-relaxed"
                  style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                  data-testid="email-preview-body"
                >{emailReady.body}</pre>
              </FieldRow>
              <div
                className="text-[11px] rounded-md px-3 py-2 leading-snug"
                style={{ background: '#FEF3C7', color: '#78350F', border: '1px solid #FCD34D' }}
              >
                A snapshot of this message has been stored on the diary entry — you&apos;ll see it in the day&apos;s PDF export.
              </div>
            </div>

            <div
              className="flex flex-col gap-2 px-4 py-3"
              style={{ background: PAPER.card, borderTop: `1px solid ${PAPER.border}` }}
            >
              <a
                href={buildOutlookUrl(emailReady)}
                onClick={finishAfterEmail}
                className="w-full text-[14px] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 active:scale-[0.98] no-underline"
                style={{ background: PAPER.navy, color: PAPER.accent }}
                data-testid="email-open-in-outlook-btn"
              >
                <Mail className="w-4 h-4" strokeWidth={2.5} />
                Open in Outlook
              </a>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={finishAfterEmail}
                  className="flex-1 text-[14px] font-medium rounded-xl py-3 hover:bg-[#FAF7F2] transition-colors"
                  style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                  data-testid="email-skip-send-btn"
                >
                  Not now
                </button>
                <a
                  href={buildMailtoUrl(emailReady)}
                  onClick={finishAfterEmail}
                  className="flex-1 text-[14px] font-medium rounded-xl py-3 flex items-center justify-center gap-2 no-underline"
                  style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                  data-testid="email-open-in-mail-btn"
                >
                  <Mail className="w-4 h-4" strokeWidth={2} />
                  Default mail
                </a>
              </div>
            </div>
          </>
        ) : (
        <>
        {/* ============ Normal entry-form view ============ */}
        <div
          className="flex items-center gap-3 px-4 py-3.5"
          style={{ background: PAPER.card, borderBottom: `1px solid ${PAPER.border}` }}
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: meta.bg, color: meta.fg }}
          >
            <Icon className="w-4.5 h-4.5" strokeWidth={2.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
              {isEditing ? (readOnly ? 'View entry' : 'Edit entry') : 'New entry'}
            </div>
            <div className="text-[15px] font-semibold" style={{ color: PAPER.ink }}>{meta.label}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-black/5 transition-colors"
            style={{ color: PAPER.muted }}
            data-testid="entry-dialog-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3.5 overflow-y-auto flex-1">
          {restoredDraft && (
            <div
              className="text-[12px] rounded-md px-3 py-2"
              style={{ background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E' }}
            >
              Restored an unfinished draft.
            </div>
          )}
          {/* DIA-2 §2.1 — read-only guard as an inline conditional (NOT a
              component defined during render) so typing never remounts the
              form and never drops input focus. */}
          {readOnly ? (
            <fieldset
              disabled
              className="border-0 p-0 m-0 min-w-0"
              style={{ pointerEvents: 'none' }}
              data-testid="entry-dialog-readonly-form-guard"
            >
              {renderTypeForm()}
            </fieldset>
          ) : (
            renderTypeForm()
          )}

          {/* DIA-2 §2.1 — saved photos gallery. In read-only mode this is
              the ONLY photo UI (no add / no remove). In edit mode we still
              render it above the fresh-upload queue so an editor sees what
              is already attached. */}
          {savedPhotos.length > 0 && (
            <div data-testid="entry-dialog-saved-photos">
              <label className="text-[11px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: PAPER.muted }}>
                Photos <span style={{ color: PAPER.hint }}>· {savedPhotos.length}</span>
              </label>
              <div className="grid grid-cols-4 gap-2">
                {savedPhotos.map((photo, idx) => (
                  <button
                    key={photo.id || photo.web_url || idx}
                    type="button"
                    onClick={() => { const items = entryPhotoItems(entry.id, savedPhotos); if (items.length) setLightbox({ items, index: Math.min(idx, items.length - 1) }); }}
                    className="aspect-square rounded-md overflow-hidden block transition-transform active:scale-95"
                    title={photo.name || 'View photo'}
                    data-testid={`entry-dialog-photo-${photo.id || idx}`}
                  >
                    <AuthThumb
                      endpoint={photo.id && entry?.id ? `/diary/entries/${entry.id}/photos/${photo.id}/thumb` : null}
                      enabled={!!(photo.id && entry?.id)}
                      testKey={photo.id || `${idx}`}
                      placeholderIcon={ImageIcon}
                      alt={photo.name || 'Photo'}
                      className="w-full h-full rounded-md"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Photo picker — hidden in read-only mode. */}
          {!readOnly && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
                {savedPhotos.length > 0 ? 'Add more photos' : 'Photos'} <span style={{ color: PAPER.hint }}>(optional)</span>
              </label>
              <label
                className="text-[12px] px-2.5 py-1.5 rounded-md cursor-pointer inline-flex items-center gap-1.5 transition-colors hover:bg-[#FAF7F2]"
                style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                data-testid="entry-dialog-photo-btn"
              >
                <Camera className="w-3.5 h-3.5" />
                Add
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={addPhotos}
                  className="hidden"
                />
              </label>
            </div>
            {queuedPhotos.length === 0 ? (
              <div
                className="text-[12px] rounded-md px-3 py-3 text-center"
                style={{ border: `1px dashed ${PAPER.border}`, background: PAPER.card, color: PAPER.muted }}
              >
                Tap Add to attach photos (camera or library)
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {queuedPhotos.map(p => (
                  <div
                    key={p.key}
                    className="relative group aspect-square rounded-md overflow-hidden"
                    style={{ border: `1px solid ${PAPER.border}` }}
                  >
                    <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeQueuedPhoto(p.key)}
                      className="absolute top-1 right-1 rounded p-1 opacity-90 hover:opacity-100"
                      style={{ background: 'rgba(0,0,0,0.6)', color: '#FFFFFF' }}
                      title="Remove"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
              Title <span style={{ color: PAPER.hint }}>(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={placeholderTitleFor(activeType)}
              className={INPUT_CLASS}
              disabled={readOnly}
              data-testid="entry-dialog-title-input"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
              Notes <span style={{ color: PAPER.hint }}>(optional)</span>
            </label>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder={"Type freely — use\n• for bullets\n[ ] for checkboxes"}
              rows={activeType === 'note' ? 8 : 4}
              className={`${INPUT_CLASS} font-mono leading-relaxed`}
              disabled={readOnly}
              data-testid="entry-dialog-detail-input"
            />
          </div>
        </div>

        {readOnly ? (
          <div
            className="flex flex-col gap-2 px-4 py-3"
            style={{ background: PAPER.card, borderTop: `1px solid ${PAPER.border}` }}
          >
            <div
              className="text-[13px] px-3 py-2.5 rounded-lg text-center"
              style={{ background: PAPER.paperMuted, color: PAPER.inkSoft, border: `1px dashed ${PAPER.border}` }}
              data-testid="entry-dialog-readonly-banner"
            >
              Read-only — you can only edit your own diary entries.
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full text-[14px] font-medium rounded-xl py-3 transition-colors hover:bg-[#FAF7F2]"
              style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
              data-testid="entry-dialog-close-btn"
            >
              Close
            </button>
          </div>
        ) : (
          <div
            className="flex flex-col gap-2 px-4 py-3"
            style={{ background: PAPER.card, borderTop: `1px solid ${PAPER.border}` }}
          >
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex-1 text-[14px] font-medium rounded-xl py-3 transition-colors hover:bg-[#FAF7F2] disabled:opacity-50"
                style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
                data-testid="entry-dialog-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 text-[14px] font-semibold rounded-xl py-3 flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-50"
                style={{ background: PAPER.navy, color: PAPER.accent }}
                data-testid="entry-dialog-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadProgress && <span className="text-[12px]">{uploadProgress}</span>}
                  </>
                ) : (isEditing ? 'Save changes' : 'Save entry')}
              </button>
            </div>
            {isEditing && canDelete && onDeleteRequest && (
              <button
                type="button"
                onClick={() => onDeleteRequest(entry)}
                disabled={saving}
                className="w-full mt-1 text-[13px] font-medium rounded-xl py-2.5 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                style={{ background: 'transparent', border: `1px dashed #FCA5A5`, color: '#B91C1C' }}
                data-testid="entry-dialog-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete entry
              </button>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

// Field row used inside the email-preview view.
const FieldRow = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#8B8378' }}>
      {label}
    </label>
    {children}
  </div>
);

// -------- Helpers ---------------------------------------------------------

/**
 * Fallback title used when the user hasn't typed one. Renders as the
 * headline on the timeline so it should be terse but informative.
 */
function defaultTitleFor(type, payload) {
  const p = payload || {};
  switch (type) {
    case 'labour':      return p.people?.length ? `${p.people.length} on site` : 'Labour';
    case 'delivery':    return p.supplier ? `Delivery from ${p.supplier}` : 'Delivery';
    case 'weather':     return p.condition || 'Weather';
    case 'delay':       return p.cause ? `Delay — ${p.cause}` : 'Delay';
    case 'rfi':         return p.raised_with ? `RFI — ${p.raised_with}` : 'RFI';
    case 'instruction': return p.party ? `${p.direction === 'issued' ? 'Instruction to' : 'Instruction from'} ${p.party}` : 'Instruction';
    case 'visit':       return p.attendees ? `Site visit — ${p.attendees}` : 'Site visit';
    case 'materials':   {
      const intent = (p.intent || 'quote') === 'order' ? 'Order' : 'Quote';
      return p.supplier_name ? `${intent} — ${p.supplier_name}` : `Materials ${intent.toLowerCase()}`;
    }
    default:            return 'Note';
  }
}

function placeholderTitleFor(type) {
  switch (type) {
    case 'labour':      return 'e.g. Framing crew · 4 on site';
    case 'delivery':    return 'e.g. Timber from Travis Perkins';
    case 'weather':     return 'e.g. Heavy rain 10am–2pm';
    case 'delay':       return 'e.g. Access blocked by neighbour skip';
    case 'rfi':         return 'e.g. Steel size for opening B';
    case 'instruction': return 'e.g. Change kitchen window to full height';
    case 'visit':       return 'e.g. Client site walk-round';
    case 'materials':   return 'e.g. Timber for first fix';
    default:            return 'Short summary';
  }
}
